import { useMemo } from 'react';
import { Button } from 'primereact/button';
import { AgendaAppointment, AgendaSchedule } from '../types/agenda.types';
import { EAppointmentStateValues, EAppointmentState } from '../../../enums/EAppointmentState';
import { formatTime } from '../utils/formatUtils';
import AvailabilityPanel from './AvailabilityPanel';
import './ScheduleView.scss';

interface ScheduleViewProps {
  appointments: AgendaAppointment[];
  selectedAgendas: AgendaSchedule[];
  buildingId?: number | null;
  selectedDate: Date;
  availabilityRefreshKey?: number;
  onSelectAvailabilitySlot: (time: string, agenda: AgendaSchedule) => void;
  loading: boolean;
  onRegisterArrival: (id: number) => void;
  onCallPatient: (id: number, appointment?: AgendaAppointment) => void;
  onFinishAttention: (id: number, appointment?: AgendaAppointment) => void;
  onRevertStatus?: (id: number) => void;
  onOpenDetail?: (appointment: AgendaAppointment) => void;
  selectedProfessionalId?: number | null;
}

function getTimeFromAppointment(appt: AgendaAppointment): string {
  if (appt.appointmentfromtime) return appt.appointmentfromtime.substring(0, 5);
  if (appt.appointmentdatetime) {
    const d = new Date(appt.appointmentdatetime);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return '00:00';
}

export default function ScheduleView({
  appointments,
  selectedAgendas,
  buildingId,
  selectedDate,
  availabilityRefreshKey,
  onSelectAvailabilitySlot,
  loading,
  onRegisterArrival,
  onCallPatient,
  onFinishAttention,
  onOpenDetail,
  selectedProfessionalId,
}: ScheduleViewProps) {

  // Queue categories
  const inAttentionList = useMemo(
    () => appointments.filter(
      (a) =>
        a.appointmentstateid === EAppointmentStateValues[EAppointmentState.INATTENTION] ||
        a.appointmentstateid === EAppointmentStateValues[EAppointmentState.IN_OBSERVATION],
    ),
    [appointments],
  );

  const waitingList = useMemo(
    () => appointments.filter(
      (a) =>
        a.appointmentstateid === EAppointmentStateValues[EAppointmentState.INQUEUE] ||
        a.appointmentstateid === EAppointmentStateValues[EAppointmentState.PENDING_ATTENTION],
    ),
    [appointments],
  );

  const upcomingList = useMemo(
    () => appointments.filter(
      (a) =>
        a.appointmentstateid === EAppointmentStateValues[EAppointmentState.SCHEDULLED] ||
        a.appointmentstateid === EAppointmentStateValues[EAppointmentState.CONFIRMED],
    ),
    [appointments],
  );

  const absentList = useMemo(
    () => appointments.filter(
      (a) => a.appointmentstateid === EAppointmentStateValues[EAppointmentState.MISSED],
    ),
    [appointments],
  );

  if (loading) {
    return (
      <div className="schedule-view schedule-view--loading">
        <div className="nv-loading" role="status">
          <i className="pi pi-spinner pi-spin" aria-hidden /> Cargando…
        </div>
      </div>
    );
  }

  const detailButton = (appt: AgendaAppointment) => (
    <Button
      type="button"
      text
      className="nv-icon nv-tip"
      icon="pi pi-eye"
      aria-label="Ver detalle"
      data-pr-tooltip="Ver detalle"
      onClick={(e) => { e.stopPropagation(); onOpenDetail?.(appt); }}
    />
  );

  const groupHeader = (label: string, count: number) => (
    <div className="qg__header">
      <span className="nv-strong">{label}</span>
      <span className="nv-tab-count qg__count">{count}</span>
    </div>
  );

  return (
    <div className="schedule-view">
      {/* Left: Live queue */}
      <section className="nv-card live-queue" aria-labelledby="live-queue-title">
        <h3 id="live-queue-title" className="nv-section-title">Cola en vivo</h3>

        {/* En atención */}
        {inAttentionList.length > 0 && (
          <div className="qg">
            {groupHeader('En atención', inAttentionList.length)}
            {inAttentionList.map((appt) => (
              <div
                key={appt.patientcarequeue}
                className="qc qc--inattention"
                onClick={() => onOpenDetail?.(appt)}
              >
                <div className="qc__body">
                  <div className="nv-strong qc__name">{appt.patientname}</div>
                  <div className="qc__sub">
                    <span className="nv-help">{appt.locationsname || 'Consultorio'}</span>
                    {appt.servedtime && <span className="nv-help qc__wait">{formatTime(appt.servedtime)}</span>}
                  </div>
                </div>
                <div className="qc__actions">
                  <Button
                    type="button"
                    className="buttonMain3"
                    label="Finalizar"
                    onClick={(e) => {
                      e.stopPropagation();
                      appt.patientcarequeue && onFinishAttention(appt.patientcarequeue, appt);
                    }}
                  />
                  {detailButton(appt)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* En espera */}
        {waitingList.length > 0 && (
          <div className="qg">
            {groupHeader('En espera', waitingList.length)}
            {waitingList.map((appt, idx) => (
              <div
                key={appt.patientcarequeue}
                className="qc"
                onClick={() => onOpenDetail?.(appt)}
              >
                <div className="qc__body">
                  <div className="nv-strong qc__name">{appt.patientname}</div>
                  <div className="qc__sub">
                    <span className="nv-help qc__num">{getTimeFromAppointment(appt)}</span>
                    {appt.arrivaltime && (
                      <span className={idx === 0 ? 'nv-help qc__wait qc__wait--warn' : 'nv-help qc__wait'}>
                        llegó {formatTime(appt.arrivaltime)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="qc__actions">
                  <Button
                    type="button"
                    className="buttonMain3"
                    label="Llamar"
                    onClick={(e) => {
                      e.stopPropagation();
                      appt.patientcarequeue && onCallPatient(appt.patientcarequeue, appt);
                    }}
                  />
                  {detailButton(appt)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Próximos */}
        {upcomingList.length > 0 && (
          <div className="qg">
            {groupHeader('Próximos', upcomingList.length)}
            {upcomingList.map((appt) => (
              <div
                key={appt.patientcarequeue}
                className="qc"
                onClick={() => onOpenDetail?.(appt)}
              >
                <div className="qc__body">
                  <div className="nv-strong qc__name">{appt.patientname}</div>
                  <div className="qc__sub">
                    <span className="nv-help qc__num">{getTimeFromAppointment(appt)}</span>
                    <span className="nv-help">Confirmar</span>
                  </div>
                </div>
                <div className="qc__actions">
                  <Button
                    type="button"
                    className="buttonMain3"
                    label="Registrar llegada"
                    onClick={(e) => {
                      e.stopPropagation();
                      appt.patientcarequeue && onRegisterArrival(appt.patientcarequeue);
                    }}
                  />
                  {detailButton(appt)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Ausente */}
        {absentList.length > 0 && (
          <div className="qg">
            {groupHeader('Ausente', absentList.length)}
            {absentList.map((appt) => (
              <div
                key={appt.patientcarequeue}
                className="qc"
                onClick={() => onOpenDetail?.(appt)}
              >
                <div className="qc__body">
                  <div className="nv-strong qc__name qc__name--strikethrough">{appt.patientname}</div>
                  <div className="qc__sub">
                    <span className="nv-help qc__num">{getTimeFromAppointment(appt)}</span>
                    <span className="nv-help">No asistió</span>
                  </div>
                </div>
                <div className="qc__actions">
                  <Button
                    type="button"
                    className="buttonMain3"
                    label="Reprogramar"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenDetail?.(appt);
                    }}
                  />
                  {detailButton(appt)}
                </div>
              </div>
            ))}
          </div>
        )}

        {inAttentionList.length === 0 && waitingList.length === 0 && upcomingList.length === 0 && absentList.length === 0 && (
          <p className="nv-muted live-queue__empty">Sin turnos en cola.</p>
        )}
      </section>

      {/* Right: Availability */}
      <AvailabilityPanel
        selectedAgendas={selectedAgendas}
        buildingId={buildingId}
        selectedDate={selectedDate}
        refreshKey={availabilityRefreshKey}
        onSelectSlot={onSelectAvailabilitySlot}
        selectedProfessionalId={selectedProfessionalId}
      />
    </div>
  );
}
